import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Search, Phone, Mail, MapPin, ArrowLeft, ChevronRight, Users, Building2 } from 'lucide-react';

/**
 * Telemarketing Territory Lists — mirrors Access TelemarketingByAreaCodeList /
 * TelemarketingBySuburbList / TelemarketingByCityList.
 *
 * Pick a suburb, area code, or city → get a sortable call list with phone numbers
 * and last-appraisal context. Designed for door-knocking and territory dialing.
 */
export default function TerritoryListsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [groupBy, setGroupBy] = useState('suburb');
  const [groups, setGroups] = useState([]);
  const [active, setActive] = useState(null);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingRows, setLoadingRows] = useState(false);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    try {
      const { data } = await api.get(`/telemarketing/territory?group_by=${groupBy}&q=${search}&limit=80`);
      setGroups(data.groups || []);
    } catch (e) { console.error(e); }
    setLoadingGroups(false);
  }, [api, groupBy, search]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const loadTerritory = async (label) => {
    setActive(label);
    setLoadingRows(true);
    try {
      const { data } = await api.get(`/telemarketing/territory/${encodeURIComponent(label)}?group_by=${groupBy}`);
      setRows(data.rows || []);
    } catch (e) { console.error(e); }
    setLoadingRows(false);
  };

  if (active) {
    return (
      <div data-testid="territory-detail-page" className="space-y-4">
        <button onClick={() => { setActive(null); setRows([]); }} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#D35400] transition-colors font-medium">
          <ArrowLeft size={16} /> Back to territories
        </button>
        <div className="flex items-center justify-between">
          <div>
            <p className="eyebrow">{groupBy === 'area_code' ? 'Area code' : groupBy === 'city' ? 'City' : 'Suburb'}</p>
            <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">{active}</h1>
            <p className="text-sm text-slate-500 mt-1 font-medium"><span className="font-mono-num font-bold text-slate-700">{rows.length}</span> {rows.length === 1 ? 'client' : 'clients'} ready to call</p>
          </div>
        </div>
        {loadingRows ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
          <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
            <table className="w-full">
              <thead><tr className="bg-slate-50/80">
                {['Client', 'Mobile', 'Home / Work', 'Address', 'Last Appraisal', 'Last Status'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.client_id} data-testid={`territory-row-${r.client_id}`} className="border-t border-[#E2E2DF] hover:bg-orange-50/30 cursor-pointer transition-colors group" onClick={() => navigate(`/clients/${r.client_id}`)}>
                    <td className="px-4 py-3 relative">
                      <div className="absolute inset-y-0 left-0 w-[2px] bg-[#D35400] opacity-0 group-hover:opacity-100 transition-opacity" />
                      <p className="text-sm font-semibold text-slate-800">{r.name}</p>
                      {r.do_not_call && <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-rose-600 mt-1 inline-block">DO NOT CALL</span>}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono-num">{r.phone_mobile || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-600 font-mono-num">{r.phone_home || <span className="text-slate-300">—</span>}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{r.address}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 font-mono-num">{r.last_appraisal || <span className="text-slate-300">never</span>}</td>
                    <td className="px-4 py-3"><span className="text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200">{r.last_status || '—'}</span></td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center"><p className="text-sm text-slate-400">No call-ready clients in {active}</p></td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="territory-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Call Territories</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Pick a {groupBy} → get a sortable call list with phones and last appraisal context</p>
        </div>
        <div className="flex gap-1 bg-white border border-[#E2E2DF] rounded-md p-0.5 shadow-sm">
          {[['suburb', 'Suburb'], ['city', 'City'], ['area_code', 'Area Code']].map(([k, l]) => (
            <button key={k} onClick={() => setGroupBy(k)} className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${groupBy === k ? 'bg-[#D35400] text-white' : 'text-slate-500 hover:text-slate-700'}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input data-testid="territory-search" value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${groupBy}...`} className="w-full pl-10 pr-4 py-2.5 border border-[#E2E2DF] rounded-md bg-white text-sm" />
      </div>

      {loadingGroups ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {groups.map(g => (
            <button key={g.label} data-testid={`territory-card-${g.label}`} onClick={() => loadTerritory(g.label)} className="bg-white border border-[#E2E2DF] rounded-md p-4 card-lift relative overflow-hidden text-left group">
              <div className="absolute inset-x-0 top-0 h-[3px] bg-[#D35400]" />
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="w-9 h-9 rounded-md bg-orange-50 flex items-center justify-center ring-1 ring-orange-100 flex-shrink-0">
                  <MapPin size={15} className="text-[#D35400]" strokeWidth={2} />
                </div>
                <ChevronRight size={14} className="text-slate-300 group-hover:text-[#D35400] transition-colors mt-1" />
              </div>
              <p className="text-sm font-bold text-slate-800 truncate" title={g.label}>{g.label}</p>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs text-slate-500 flex items-center gap-1"><Building2 size={11} /><span className="font-mono-num font-semibold">{g.count}</span></span>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Users size={11} /><span className="font-mono-num font-semibold">{g.unique_clients}</span></span>
              </div>
            </button>
          ))}
          {groups.length === 0 && <div className="col-span-full text-center py-12 text-slate-400">No territories found</div>}
        </div>
      )}
    </div>
  );
}
